"use client";

import { useEffect } from "react";
import posthog from "posthog-js";
import {
  trackCompareEvent,
  type CompareClickEventName,
} from "@/lib/compare/analytics";

/**
 * #1857 — click delegator for the first-party Help evidence module.
 *
 * The module exists to prove comparison claims to a sceptical buyer, including
 * on two paid ad landings, and until now nothing recorded whether anyone
 * followed it: the links carried no `data-compare-event`, so neither
 * <CompareTracker> nor <CompareHubTracker> saw them, and the module's testid is
 * not in COMPARE_SECTION_TESTIDS so no section-view fired either. Only
 * PostHog's generic `$autocapture` remained.
 *
 * Why its own delegator rather than a new entry in <CompareTracker>: of the
 * four surfaces that render <CompareHelpLinks>, only the /compare/* detail
 * pages mount <CompareTracker> and only the hub mounts <CompareHubTracker>.
 * The two ad landings mount neither — and they are exactly the surfaces the
 * signal is wanted for. Mounting this from inside <CompareHelpLinks> means the
 * module carries its own instrumentation, so a fifth surface gets it for free.
 *
 * `help_link` is deliberately absent from <CompareTracker>'s CLICK_ATTR_VALUES
 * so a detail page (which mounts both) captures the click exactly once.
 *
 * Several instances can render on one page (a /compare/* detail page carries
 * one per first-party product card), so the document listener is refcounted —
 * otherwise one click would capture once per mounted instance.
 *
 * Consent: same posture as <CompareHubTracker> — the `posthog.__loaded` guard
 * inside trackCompareEvent plus the provider's `before_send` barrier are the
 * dispatch gate. No duplicate consent plumbing here (49-CONTEXT.md Grey Area 1).
 */

const HELP_LINK_ATTR: CompareClickEventName = "help_link";

let listenerCount = 0;
let sharedListener: ((e: MouseEvent) => void) | null = null;

function onClick(e: MouseEvent) {
  const target = e.target;
  if (!(target instanceof Element)) return;
  const anchor = target.closest<HTMLAnchorElement>(
    `a[data-compare-event="${HELP_LINK_ATTR}"]`,
  );
  if (!anchor) return;

  // The registry-authored href, not `anchor.href` — we want `/help/x/y`, not an
  // origin-qualified URL that would vary between preview and production.
  const targetHref = anchor.getAttribute("href");
  const surface = anchor.getAttribute("data-compare-surface");
  if (!targetHref || !surface) return;

  trackCompareEvent({
    event: "compare_help_link_clicked",
    target_href: targetHref,
    surface,
  });
}

export function CompareHelpLinkTracker() {
  useEffect(() => {
    if (listenerCount === 0) {
      sharedListener = onClick;
      document.addEventListener("click", sharedListener);
    }
    listenerCount += 1;

    // Touch posthog to document the capture-path dependency (the wrapper
    // handles the __loaded guard itself — see lib/compare/analytics.ts).
    void posthog;

    return () => {
      listenerCount -= 1;
      if (listenerCount === 0 && sharedListener) {
        document.removeEventListener("click", sharedListener);
        sharedListener = null;
      }
    };
  }, []);

  return null;
}
